import { useQuery } from '@tanstack/react-query';
import cn from 'classnames';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { getCandidateCommentaryOptions } from 'client/@tanstack/react-query.gen';
import { IconInfo } from 'components/UI/Icons/Info';
import { IconSparkles } from 'components/UI/Icons/Sparkles';
import { Button } from 'components/UI/Tailwind/Button';
import { getFormattedDate } from 'helpers/date';
import { IconPlus } from 'components/UI/Icons/Plus';
import { FormRadioGroup } from 'components/UI/Tailwind/FormRadioGroup';
import { FormCheckbox } from 'components/UI/Tailwind/FormCheckbox';
import { zIntroEmailType } from 'client/zod.gen';
import { Schema as ParentSchema } from './Step2';
import { IconAlertTriangle } from 'components/UI/Icons/AlertTriangle';
import {
  TailoredHighlights,
  tailoredHighlightSchema,
} from './TailoredHighlights';

export const schema = z.object({
  about_uuid: z.string(),
  highlights_with_selected: z.record(
    z.string(),
    z
      .union([z.boolean(), z.undefined()])
      .transform((val: any): val is boolean => Boolean(val)),
  ),
  intro_email_type: zIntroEmailType,
  // AI-drafted highlights for this pipeline; undefined until drafted or loaded.
  tailored_highlights: z.array(tailoredHighlightSchema).optional(),
  // The drafting run already copied in, so edits aren't overwritten.
  tailored_task_id: z.string().optional(),
});

export type Schema = z.infer<typeof schema>;

export function FormCommentary({
  candidateUuid,
  className,
  showBulkActions,
  showLastModified,
  onEdit,
}: Pick<JSX.IntrinsicElements['div'], 'className'> & {
  candidateUuid: string;
  onEdit: VoidFunction;
  showBulkActions?: boolean;
  showLastModified?: boolean;
}) {
  const { t } = useTranslation();

  const { setValue, watch } = useFormContext<ParentSchema>();
  const watchHighlights = watch(
    `candidates.${candidateUuid}.highlights_with_selected`,
  );

  const queryHighlights = useQuery({
    ...getCandidateCommentaryOptions({
      path: {
        candidate_uuid: candidateUuid,
      },
    }),
    select: (data) => data.highlights,
  });

  const queryTemplates = useQuery({
    enabled: Boolean(candidateUuid),
    ...getCandidateCommentaryOptions({
      path: {
        candidate_uuid: candidateUuid ?? '',
      },
    }),
    select: (data) =>
      data.introduction_templates.filter((template) => !template.hidden),
  });

  const onAllHighlights = (select: boolean) => {
    if (!watchHighlights) {
      return;
    }
    const newHighlights = Object.keys(watchHighlights).reduce(
      (acc, highlight) => ({
        ...acc,
        [highlight]: select,
      }),
      {} as Record<string, boolean>,
    );

    setValue(
      `candidates.${candidateUuid}.highlights_with_selected`,
      newHighlights,
      { shouldValidate: true },
    );
  };

  const isHighlightsAllSelected = Object.values(watchHighlights ?? {}).some(
    Boolean,
  );

  return (
    <>
      <div className="flex flex-col gap-y-4 border-b border-b-gray-300 pb-8">
        <div
          className={cn('flex items-center justify-between pb-4', className)}
        >
          <div className="flex items-center gap-x-2 text-gray-600 [&>svg]:h-[18px] [&>svg]:w-[18px]">
            <IconInfo />
            <span className="text-14/100% font-medium">
              {t('layout.about')}
            </span>
          </div>
        </div>
        {queryTemplates.data && queryTemplates.data.length > 0 && (
          <FormRadioGroup
            variant="radio"
            name={`candidates.${candidateUuid}.about_uuid`}
            options={queryTemplates.data.map((template) => ({
              label: (
                <div className="flex flex-1 flex-col gap-y-2 rounded-[8px] bg-gray-50 p-4">
                  {showLastModified && template.last_modified && (
                    <span className="flex flex-1 items-center justify-between text-12/100% font-normal text-gray-600">
                      <span className="text-heading-25">{template.title}</span>

                      <span>
                        Last updated {getFormattedDate(template.last_modified)}
                      </span>
                    </span>
                  )}
                  <span className="text-left">{template.body}</span>
                </div>
              ),
              value: template.uuid,
            }))}
          />
        )}

        {!queryTemplates.data?.length && (
          <span className="text-14/100% font-normal text-gray-500">
            {t('candidatePopover.templates_empty')}
          </span>
        )}
        <span>
          <Button
            variant="default-link"
            iconStart={<IconPlus />}
            size={32}
            onClick={onEdit}
          >
            {t('candidatePopover.addTemplate')}
          </Button>
        </span>
      </div>

      <div className="flex flex-col gap-y-4">
        <div className="flex items-center justify-between py-4">
          <div className="flex h-7 items-center gap-x-2 text-gray-600 [&>svg]:h-[18px] [&>svg]:w-[18px]">
            <IconSparkles />
            <span className="text-14/100% font-medium">
              {t('form.candidateHighlights.panel.highlights')}
            </span>
          </div>
          {Object.keys(watchHighlights ?? {}).length > 0 && showBulkActions && (
            <Button
              onClick={() => onAllHighlights(!isHighlightsAllSelected)}
              size={32}
              variant="default-quaternary"
            >
              {isHighlightsAllSelected
                ? t('candidatePopover.deselectAll')
                : t('candidatePopover.selectAll')}
            </Button>
          )}
        </div>

        <TailoredHighlights candidateUuid={candidateUuid} />

        {Boolean(queryHighlights.data?.length) && (
          <span className="text-12/100% font-medium text-gray-600">
            From the profile
          </span>
        )}

        {Boolean(queryHighlights.data?.length) && (
          <ul className="flex flex-col gap-y-4">
            {queryHighlights.data?.map((highlight, index) => {
              if (
                highlight.soft_delete &&
                !Object.keys(watchHighlights ?? {}).includes(highlight.uuid)
              ) {
                return null;
              }

              return (
                <li key={highlight.uuid} className="flex flex-col">
                  <label className="group flex flex-1 gap-x-4 hover:cursor-pointer">
                    <FormCheckbox
                      className="flex flex-col"
                      name={`candidates.${candidateUuid}.highlights_with_selected.${highlight.uuid}`}
                    />

                    <div className="flex flex-1 flex-col gap-y-2 rounded-[8px] bg-gray-50 p-4">
                      {highlight.soft_delete && (
                        <span className="flex items-center gap-x-2 text-yellow-800 [&>svg]:h-[14px] [&>svg]:w-[14px]">
                          <IconAlertTriangle />
                          <span>
                            This is deleted from the profile and might be
                            outdated.
                          </span>
                        </span>
                      )}
                      {showLastModified && highlight.last_modified && (
                        <span className="flex flex-1 items-center justify-between text-12/100% font-normal text-gray-600">
                          <span className="text-heading-25">
                            {`Highlight ${index + 1}`}
                          </span>

                          <span>
                            Last updated{' '}
                            {getFormattedDate(highlight.last_modified)}
                          </span>
                        </span>
                      )}
                      <span className="text-left">{highlight.highlight}</span>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        {!queryHighlights.data?.filter((highlight) => !highlight.soft_delete)
          .length && (
          <span className="text-14/100% font-normal text-gray-500">
            {t('candidatePopover.highlights_empty')}
          </span>
        )}
        <span>
          <Button
            variant="default-link"
            iconStart={<IconPlus />}
            size={32}
            onClick={onEdit}
          >
            {t('candidatePopover.addHighlight')}
          </Button>
        </span>
      </div>
    </>
  );
}
